<template>
  <div class="animated fadeIn">
    <!-- <b-card no-body> -->
    <b-spinner v-if="spinners.loading" type="grow" variant="primary" />
    <NoDataView v-else-if="!tableItems.length" />
    <div v-else>
      <b-button :disabled="isCreatingCR" variant="primary" class="mb-2" @click="createCR">
        New CR
      </b-button>
      <b-table-simple class="table-sm text-center mb-0" bordered hover>
        <b-thead>
          <b-tr>
            <b-th v-for="c in tableFields()" :key="c.key" :class="[...[c.thClass], ...[c.class]]">{{ c.label }}</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <template v-for="(row) in tableItems">
            <ProjectFinanceTableRowForm v-if="isEditing?.id == row.id" :key="`row-${row.id}`" :row="clone(row)"
              :jira_key="jira_key" :tableFields="tableFields" @cancel="onEditCanceled" @saved="crSaved" />
            <b-tr v-else :key="`row-${row.id}`">
              <template v-for="c in tableFields(row.isCR)">
                <b-td :key="`td-${row.id}-${c.key}`" :colspan="c.colspan" :class="[...[c.tdClass], ...[c.class]]">
                  <template v-if="c.key === 'options'">
                    <b-link v-if="!row.isCR" :to="{ name: 'ProjectHome', params: { jira_key, cat: 'settings' } }">
                      <i class="mx-1 fa fa-edit fa-lg" />
                    </b-link>
                    <b-row v-else-if="!isEditing">
                      <b-col>
                        <b-link @click="edit(row.id, row.isCR)">
                          <i class="mx-1 fa fa-edit fa-lg" />
                        </b-link>
                        <b-link @click="confirmDelete(row.id)">
                          <i class="fa fa-lg fa-trash action"></i>
                        </b-link>
                      </b-col>
                    </b-row>
                  </template>
                  <template v-else-if="['start_date', 'end_date'].includes(c.key)">{{
      moment(row[c.key]).format('yyyy-MM-DD') }}</template>
                  <template v-else-if="c.key === 'client_id'">{{ row.client?.name }}</template>
                  <template v-else-if="c.key === 'category_id'">{{ row.category?.name }}</template>
                  <template v-else-if="c.key === 'baseline_costs'">
                    <Currency :value="row[c.key]" />
                  </template>
                  <template v-else-if="c.key === 'additional_actuals'">
                    <Currency :value="row[c.key]" />
                  </template>
                  <template v-else-if="c.key === 'actuals'">
                    <i v-if="row.overlaps" class="fa fa-lg fa-warning text-danger" v-b-tooltip.hover
                      :title="$dt('project.finances.overlap-tooltip', 'Period overlaps other - actuals may not be used!')"></i>
                    <Currency :value="row[c.key] / 100" :class="{ 'text-muted': row.overlaps }" />
                  </template>
                  <template v-else-if="c.key === 'profit'">
                    <i v-if="row.overlaps" class="fa fa-lg fa-warning text-danger" v-b-tooltip.hover
                      :title="$dt('project.finances.overlap-profit-tooltip', 'Period overlaps other - profit cannot be calculated!')"></i>
                    <template v-if="!row.overlaps && row.actuals">
                      {{ Math.round((row.contract_budget - (row.additional_actuals + row.actuals / 100)) /
      row.contract_budget * 1000) / 10 }} %
                    </template>
                  </template>
                  <template v-else-if="c.key === 'contract_budget'">
                    <Currency :value="row[c.key]" />
                  </template>
                  <template v-else>{{ row[c.key] }}</template>
                </b-td>
              </template>
            </b-tr>
          </template>
        </b-tbody>
        <b-tfoot>
          <b-tr>
            <template v-for="c in sumFields()">
              <b-td :key="`tfoot-td-${c.key}`" :colspan="c.colspan" :class="[...[c.tdClass], ...[c.class]]">
                <template v-if="c.sum && columnSums[c.key]">
                  <currency :value="columnSums[c.key]" />
                </template>
              </b-td>
            </template>
          </b-tr>
        </b-tfoot>
      </b-table-simple>
    </div>
    <b-modal ref="confirmModal" okVariant="danger" title="Delete confirmation" @ok="deleteCr">
      <p class="my-4">Are you sure you want to delete this change request?</p>
    </b-modal>
  </div>
</template>

<script>
import clone from 'lodash.clonedeep';
import Vue from 'vue';
import moment from 'moment';
import Services from '@/services/index';
import ProjectFinanceTableRowForm from '@/views/Project/ProjectFinanceTableRowForm.vue';
import NoDataView from '@/views/Shared/NoDataView.vue';
import Currency from '../Shared/Currency.vue';

export default {
  created() {
    this.moment = moment;
  },
  mounted() {
    this.load();
  },
  data() {
    return {
      spinners: {
        loading: false,
        // isSaving: false,
      },
      filters: {
        sort_field: null,
        sort_order: null,
        search: '',
      },
      project: {
        // changeRequests: []
      },
      changeRequests: [],
      isEditing: null,
      isCreatingCR: false,
      isDeleting: null,
    };
  },
  props: {
    jira_key: { type: String, required: true },
  },
  components: {
    ProjectFinanceTableRowForm,
    NoDataView,
    Currency
  },
  computed: {

    tableItems() {
      return [
        this.project,
        ...this.changeRequests.map((e) => { e.isCR = true; return e; }),
        ...(this.isCreatingCR ? [{
          id: 0,
          project_id: this.project.id,
          start_date: null,
          end_date: null,
          baseline_costs: 0,
          additional_actuals: 0,
          actuals: 0,
          contingency: 0,
          contract_budget: 0,
          name: '',
          contract_id: null,
          vat_rate: 0,
          payment_due_days: 0,
          contract: null,
          isCR: true,
        }] : []),
      ];
    },
    columnSums() {
      return this.project.id
        ? this.tableFields(false).filter((f) => f.sum).map((f) => f.key)
          .reduce((sums, f) => { return { ...sums, [f]: this.project[f] + this.changeRequests.reduce((s, cr) => { return s + cr[f]; }, 0) }; }, {})
        : {};
    },
  },
  methods: {
    clone: clone,
    sumFields() {
      return this.tableFields(false).reduce((fields, f) => {
        if (!f.sum) {
          if (fields.length === 0 || fields[fields.length - 1].sum) {
            fields.push(f);
          } else {
            fields[fields.length - 1].colspan = fields[fields.length - 1].colspan + 1;
          }
        } else {
          fields.push(f);
        }
        return fields;
      }, []);
    },
    tableFields(isCR) {
      isCR = isCR || false;
      return [
        { spanIfCR: 4, key: 'name', label: this.$dt('project_finance_table.name', 'Name'), tdClass: 'text-left', thClass: 'fhead' },
        { spanIfCR: null, key: 'projectTypeKey', label: this.$dt('project_finance_table.projectTypeKey', 'Type') },
        { spanIfCR: null, key: 'category_id', label: this.$dt('project_finance_table.category_id', 'Category') },
        { spanIfCR: null, key: 'client_id', label: this.$dt('project_finance_table.client_id', 'Client') },
        { spanIfCR: 1, class: 'w-8 d-none d-xl-table-cell', key: 'start_date', label: this.$dt('project_finance_table.start_date', 'Start Date') },
        { spanIfCR: 1, class: 'w-8 d-none d-xl-table-cell', key: 'end_date', label: this.$dt('project_finance_table.end_date', 'End Date') },
        { spanIfCR: 1, sum: true, class: 'w-10 d-none d-xl-table-cell', key: 'baseline_costs', label: this.$dt('project_finance_table.baseline_costs', 'Baseline Costs'), tdClass: 'text-right' },
        { spanIfCR: 1, sum: true, class: 'w-10 d-none d-xl-table-cell', key: 'additional_actuals', label: this.$dt('project_finance_table.additional_actuals', 'Additional actuals'), tdClass: 'text-right' },
        { spanIfCR: 1, sum: true, class: 'w-10 d-none d-xl-table-cell', key: 'actuals', label: this.$dt('project_finance_table.actuals', 'Actuals'), tdClass: 'text-right' },
        { spanIfCR: 1, sum: true, class: 'w-10 d-none d-xl-table-cell', key: 'profit', label: this.$dt('project_finance_table.profit', 'Profit'), tdClass: 'text-right' },
        // { spanIfCR: 1, key: 'contingency', label: this.$dt('project_finance_table.contingency', 'Contingency') },
        { spanIfCR: 1, sum: true, class: 'w-10 d-none d-xl-table-cell', key: 'contract_budget', label: this.$dt('project_finance_table.contract_budget', 'Contract budget'), tdClass: 'text-right' },
        { spanIfCR: 1, class: 'w-6 d-none d-xl-table-cell', key: 'vat_rate', label: this.$dt('project_finance_table.vat_rate', 'VAT (%)') },
        // { spanIfCR: 1, key: 'contract.contract_nr', label: this.$dt('project_finance_table.contract.contract_nr', 'Contract Nr.') },
        { spanIfCR: 1, class: 'w-6 d-none d-xl-table-cell', key: 'payment_due_days', label: this.$dt('project_finance_table.payment_due_days', 'Payment Due (days)') },
        { spanIfCR: 1, class: 'w-10', key: 'options', label: this.$dt('global.options', 'Actions'), tdClass: 'options' },
      ]
        .filter((e) => !isCR || (isCR && e.spanIfCR !== null))
        .map((e) => {
          e.colspan = (isCR && e.spanIfCR !== null) ? e.spanIfCR : 1;
          return e;
        });
    },
    load() {
      this.spinners.loading = true;
      Services.projects.info_full(this.jira_key).then(({ data }) => {
        this.spinners.loading = false;
        this.project = data.project;
        this.changeRequests = data.project.changeRequests.map((cr) => {
          cr.overlaps = !!data.project.changeRequests.find(e => e.id != cr.id && e.end_date > cr.start_date && e.start_date < cr.end_date)
          return cr;
        });

        this.project.changeRequests = null;
        // this.project.
      }).catch((data) => {
        console.warn(data);
        this.spinners.loading = false;
      });
    },
    edit(id, isCR) {
      this.isEditing = { id, isCR };
    },
    onEditCanceled() {
      this.isEditing = null;

      if (this.isCreatingCR) {
        this.isCreatingCR = false;
      }
    },
    crSaved(row) {
      if (this.isCreatingCR) {
        this.isCreatingCR = false;
        this.isEditing = null;
        this.changeRequests.push(row);

        return;
      }

      const idx = this.changeRequests.findIndex((r) => r.id === row.id);
      const rowOld = this.changeRequests[idx];
      Vue.set(this.changeRequests, idx, { ...rowOld, ...row });
      this.isEditing = null;
    },
    createCR() {
      this.isCreatingCR = true;
      this.isEditing = { id: 0, isCR: true };
    },
    confirmDelete(id) {
      this.isDeleting = id;
      this.$refs.confirmModal.show();
    },
    deleteCr() {
      Services.projects.deleteChangeRequest(this.isDeleting).then(() => {
        this.$refs.confirmModal.hide();
        this.load();
      })
    }
  }
};
</script>

<style scoped>
.options {
  min-width: 100px;
}

.fhead {
  height: 6em;
}

.w-10 {
  width: 10%;
}

.w-8 {
  width: 8%;
}

.w-6 {
  width: 6%;
}
</style>
